import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Grid, IconButton, MenuItem, Paper,
  Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Pagination, CircularProgress, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import {
  deleteCfopFiscal, deleteIbptFiscal, getCfopsFiscais, getIbptFiscal,
  saveCfopFiscal, saveIbptFiscal,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const emptyCfop = () => ({
  codigo: '', descricao: '', naturezaOperacao: '', tipoNf: 1,
  finalidadeNfe: 1, destinoPadrao: 1, consumidorFinal: true,
  envolveCombustivel: false, cfopInterestadual: '',
});

const TIPO_NF = [
  { value: 0, label: 'Entrada' },
  { value: 1, label: 'Saída' },
];

const FINALIDADE_NFE = [
  { value: 1, label: 'Normal' },
  { value: 2, label: 'Complementar' },
  { value: 3, label: 'Ajuste' },
  { value: 4, label: 'Devolução' },
];

const DESTINO = [
  { value: 1, label: 'Interna (mesma UF)' },
  { value: 2, label: 'Interestadual' },
  { value: 3, label: 'Exterior' },
];

const optLabel = (options, value) =>
  options.find((o) => o.value === Number(value))?.label ?? '—';

const CfopSelect = ({ label, value, options, onChange, helperText }) => (
  <TextField
    select
    label={label}
    value={Number(value)}
    onChange={(e) => onChange(Number(e.target.value))}
    helperText={helperText}
    fullWidth
  >
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
);
const emptyIbpt = () => ({
  id: 0, ncm: '', descricao: '', aliquotaNacionalFederal: 0,
  aliquotaImportadosFederal: 0, aliquotaEstadual: 0, aliquotaMunicipal: 0,
});

export default function FiscalCatalogPage({ type }) {
  const isCfop = type === 'cfop';
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [termo, setTermo] = useState('');
  const [pagina, setPagina] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [cfopOptions, setCfopOptions] = useState([]);
  const [error, setError] = useState('');
  const qtde = 20;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = isCfop
        ? await getCfopsFiscais({ termo, pagina, qtde })
        : await getIbptFiscal({ termo, pagina, qtde });
      setItems(data.items || []);
      setTotalItems(data.totalItems || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao carregar cadastro fiscal.'));
    } finally {
      setLoading(false);
    }
  }, [isCfop, pagina, termo]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setSearchText('');
    setTermo('');
    setPagina(1);
  }, [type]);

  useEffect(() => {
    if (!form || !isCfop) {
      setCfopOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getCfopsFiscais({ pagina: 1, qtde: 500 });
        if (!cancelled) setCfopOptions(data.items || []);
      } catch {
        if (!cancelled) setCfopOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form, isCfop]);

  const search = () => {
    setPagina(1);
    setTermo(searchText.trim());
  };

  const edit = (item = null) => setForm(
    item
      ? { ...item, _editing: true }
      : (isCfop ? emptyCfop() : emptyIbpt()),
  );
  const change = (field) => (e) => setForm((prev) => ({
    ...prev,
    [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));
  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const save = async () => {
    try {
      const payload = isCfop
        ? {
            ...form,
            codigo: Number(form.codigo),
            tipoNf: Number(form.tipoNf),
            finalidadeNfe: Number(form.finalidadeNfe),
            destinoPadrao: Number(form.destinoPadrao),
            cfopInterestadual: form.cfopInterestadual
              ? Number(form.cfopInterestadual) : null,
          }
        : {
            ...form,
            aliquotaNacionalFederal: Number(form.aliquotaNacionalFederal),
            aliquotaImportadosFederal: Number(form.aliquotaImportadosFederal),
            aliquotaEstadual: Number(form.aliquotaEstadual),
            aliquotaMunicipal: Number(form.aliquotaMunicipal),
          };
      if (isCfop) await saveCfopFiscal(payload); else await saveIbptFiscal(payload);
      setForm(null);
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar.'));
    }
  };
  const remove = async (item) => {
    if (!window.confirm('Confirmar exclusão?')) return;
    try {
      if (isCfop) await deleteCfopFiscal(item.codigo);
      else await deleteIbptFiscal(item.id);
      if (items.length === 1 && pagina > 1) setPagina((p) => p - 1);
      else await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao excluir.'));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title={isCfop ? 'CFOP' : 'Tabela IBPT'}
        subtitle={isCfop ? 'Naturezas e regras das operações fiscais.' : 'Carga tributária aproximada por NCM e NBS.'}
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => edit()}>Novo</Button>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          label="Buscar por código ou descrição"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
          sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}
        />
        <Button variant="outlined" onClick={search}>Buscar</Button>
      </Paper>
      <ResponsiveTable>
        <Table>
          <TableHead><TableRow>
            <TableCell>{isCfop ? 'CFOP' : 'NCM/NBS'}</TableCell>
            <TableCell>Descrição</TableCell>
            {isCfop && <TableCell>Regras</TableCell>}
            {!isCfop && <TableCell>Federal nacional</TableCell>}
            {!isCfop && <TableCell>Estadual</TableCell>}
            <TableCell align="right">Ações</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isCfop ? 4 : 5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isCfop ? 4 : 5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum registro encontrado
                </TableCell>
              </TableRow>
            ) : items.map((item) => (
              <TableRow key={isCfop ? item.codigo : item.id} hover onDoubleClick={() => edit(item)}>
                <TableCell data-label={isCfop ? 'CFOP' : 'NCM/NBS'}>
                  <strong>{isCfop ? item.codigo : item.ncm}</strong>
                </TableCell>
                <TableCell data-label="Descrição">
                  <Typography variant="body2" fontWeight={600}>{item.descricao}</Typography>
                  {isCfop && item.naturezaOperacao && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.naturezaOperacao}
                    </Typography>
                  )}
                </TableCell>
                {isCfop && (
                  <TableCell data-label="Regras">
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={optLabel(TIPO_NF, item.tipoNf)} />
                      <Chip size="small" variant="outlined" label={optLabel(FINALIDADE_NFE, item.finalidadeNfe)} />
                      <Chip size="small" variant="outlined" label={optLabel(DESTINO, item.destinoPadrao)} />
                      {item.consumidorFinal && <Chip size="small" color="info" label="Cons. final" />}
                      {item.envolveCombustivel && <Chip size="small" color="warning" label="Combustível" />}
                      {item.cfopInterestadual && (
                        <Chip size="small" color="secondary" label={`Interestadual ${item.cfopInterestadual}`} />
                      )}
                    </Stack>
                  </TableCell>
                )}
                {!isCfop && <TableCell data-label="Federal nacional">{item.aliquotaNacionalFederal}%</TableCell>}
                {!isCfop && <TableCell data-label="Estadual">{item.aliquotaEstadual}%</TableCell>}
                <TableCell align="right" data-actions="">
                  <Tooltip title="Editar"><IconButton onClick={() => edit(item)}><EditIcon /></IconButton></Tooltip>
                  <Tooltip title="Excluir"><IconButton color="error" onClick={() => remove(item)}><DeleteIcon /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
      <Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Pagination
          count={totalPages}
          page={pagina}
          onChange={(_event, value) => setPagina(value)}
          color="primary"
        />
        <Typography variant="body2" color="text.secondary">
          Exibindo {items.length} de {totalItems} registros
        </Typography>
      </Stack>

      <Dialog open={Boolean(form)} onClose={() => setForm(null)} fullWidth maxWidth="md">
        <DialogTitle>{form?._editing || form?.id ? 'Editar' : 'Novo'} {isCfop ? 'CFOP' : 'registro IBPT'}</DialogTitle>
        <DialogContent dividers>
          {form && (isCfop ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Identificação</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Código CFOP"
                  value={form.codigo}
                  onChange={change('codigo')}
                  disabled={form._editing}
                  inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                  helperText="4 dígitos (ex.: 5102)"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField label="Descrição resumida" value={form.descricao} onChange={change('descricao')} fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Natureza da operação (aparece na NF-e)"
                  value={form.naturezaOperacao}
                  onChange={change('naturezaOperacao')}
                  placeholder="Ex.: Venda de mercadoria adquirida ou recebida de terceiros"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Comportamento padrão da NF-e</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <CfopSelect
                  label="Tipo da nota"
                  value={form.tipoNf}
                  options={TIPO_NF}
                  onChange={(v) => setField('tipoNf', v)}
                  helperText="Entrada ou saída"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <CfopSelect
                  label="Finalidade"
                  value={form.finalidadeNfe}
                  options={FINALIDADE_NFE}
                  onChange={(v) => setField('finalidadeNfe', v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <CfopSelect
                  label="Destino da operação"
                  value={form.destinoPadrao}
                  options={DESTINO}
                  onChange={(v) => setField('destinoPadrao', v)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={cfopOptions.filter(
                    (c) => String(c.codigo) !== String(form.codigo),
                  )}
                  getOptionLabel={(o) => `${o.codigo} — ${o.descricao}`}
                  value={
                    cfopOptions.find(
                      (c) => c.codigo === Number(form.cfopInterestadual),
                    ) || null
                  }
                  onChange={(_e, v) => setField('cfopInterestadual', v?.codigo ?? '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="CFOP interestadual equivalente"
                      helperText="Opcional. Usado quando o destinatário é de outra UF."
                    />
                  )}
                  isOptionEqualToValue={(a, b) => a.codigo === b.codigo}
                  noOptionsText="Nenhum CFOP cadastrado"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={0.5} sx={{ pt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.consumidorFinal)}
                        onChange={(e) => setField('consumidorFinal', e.target.checked)}
                      />
                    }
                    label="Operação com consumidor final"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.envolveCombustivel)}
                        onChange={(e) => setField('envolveCombustivel', e.target.checked)}
                      />
                    }
                    label="Envolve combustível (posto / ANP)"
                  />
                </Stack>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}><TextField label="NCM/NBS" value={form.ncm} onChange={change('ncm')} disabled={Boolean(form.id)} fullWidth /></Grid>
              <Grid item xs={12} md={9}><TextField label="Descrição" value={form.descricao} onChange={change('descricao')} fullWidth /></Grid>
              {[
                ['aliquotaNacionalFederal', 'Federal nacional (%)'],
                ['aliquotaImportadosFederal', 'Federal importado (%)'],
                ['aliquotaEstadual', 'Estadual (%)'],
                ['aliquotaMunicipal', 'Municipal (%)'],
              ].map(([field, label]) => <Grid item xs={6} md={3} key={field}><TextField label={label} type="number" value={form[field]} onChange={change(field)} fullWidth /></Grid>)}
            </Grid>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setForm(null)}>Cancelar</Button><Button variant="contained" onClick={save}>Salvar</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
